import { useState, useEffect } from "react";
import type { XPostTemplateResponse, CrmMediaResponse, XPostResponse } from "../../../types/crm";
import { getTemplates, composeXPost, createXPost } from "../../../services/crmXPosts";
import { MediaBrowserModal } from "./MediaBrowserModal";
import "./ComposeXPostModal.css";

interface ComposeXPostModalProps {
  isOpen: boolean;
  crmProspectProfileId: string;
  onClose: () => void;
  onSuccess: (post: XPostResponse) => void;
}

export const ComposeXPostModal = ({
  isOpen,
  crmProspectProfileId,
  onClose,
  onSuccess,
}: ComposeXPostModalProps) => {
  const [templates, setTemplates] = useState<XPostTemplateResponse[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [tweetText, setTweetText] = useState("");
  const [unresolvedVars, setUnresolvedVars] = useState<string[]>([]);
  const [attachedMedia, setAttachedMedia] = useState<CrmMediaResponse | null>(null);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTweetText("");
    setSelectedTemplateId("");
    setUnresolvedVars([]);
    setAttachedMedia(null);
    setError(null);
    setPosting(false);
    setLoadingTemplates(true);
    getTemplates()
      .then((t) => {
        setTemplates(t);
        const defaultTemplate = t.find((tpl) => tpl.isDefault);
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.id);
          composeFromTemplate(defaultTemplate.id);
        }
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTemplates(false));
  }, [isOpen]);

  const composeFromTemplate = async (templateId: string) => {
    if (!templateId) {
      setTweetText("");
      setUnresolvedVars([]);
      return;
    }
    setComposing(true);
    try {
      const result = await composeXPost({
        templateId,
        crmProspectProfileId,
      });
      setTweetText(result.previewText);
      setUnresolvedVars(result.unresolvedVariables);
    } catch {
      setError("Failed to compose from template.");
    } finally {
      setComposing(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    composeFromTemplate(templateId);
  };

  const handlePost = async () => {
    if (!tweetText.trim() || tweetText.length > 280) return;
    setPosting(true);
    setError(null);
    try {
      const result = await createXPost({
        crmProspectProfileId,
        text: tweetText.trim(),
        crmMediaId: attachedMedia?.id,
        templateId: selectedTemplateId || undefined,
      });
      if (result.status === "Failed") {
        setError(result.errorMessage || "Tweet posting failed.");
        setPosting(false);
        return;
      }
      onSuccess(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to post tweet.";
      setError(message);
      setPosting(false);
    }
  };

  if (!isOpen) return null;

  const charCount = tweetText.length;
  const isOverLimit = charCount > 280;

  return (
    <div className="compose-xpost__overlay" onClick={onClose}>
      <div className="compose-xpost" onClick={(e) => e.stopPropagation()}>
        <div className="compose-xpost__header">
          <h3>Post to X</h3>
          <button className="compose-xpost__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="compose-xpost__body">
          {/* Template picker */}
          <div className="compose-xpost__field">
            <label className="compose-xpost__label">Template</label>
            {loadingTemplates ? (
              <span className="compose-xpost__loading-text">Loading templates...</span>
            ) : (
              <select
                className="compose-xpost__select"
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                disabled={composing}
              >
                <option value="">— Write from scratch —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.isSystemDefault ? "(Default)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tweet text */}
          <div className="compose-xpost__field">
            <label htmlFor="compose-x-post-modal-tweet-text-0" className="compose-xpost__label">Tweet Text</label>
            <textarea id="compose-x-post-modal-tweet-text-0"
              className={`compose-xpost__textarea ${isOverLimit ? "compose-xpost__textarea--over" : ""}`}
              value={tweetText}
              onChange={(e) => setTweetText(e.target.value)}
              rows={4}
              placeholder="What's happening?"
              disabled={composing}
            />
            <div className="compose-xpost__char-count-row">
              {unresolvedVars.length > 0 && (
                <div className="compose-xpost__warnings">
                  {unresolvedVars.map((v) => (
                    <span key={v} className="compose-xpost__warning-badge">
                      {v} not set
                    </span>
                  ))}
                </div>
              )}
              <span className={`compose-xpost__char-count ${isOverLimit ? "compose-xpost__char-count--over" : ""}`}>
                {charCount}/280
              </span>
            </div>
          </div>

          {/* Media attachment */}
          <div className="compose-xpost__field">
            <label className="compose-xpost__label">Media (optional)</label>
            {attachedMedia ? (
              <div className="compose-xpost__attached-media">
                {attachedMedia.thumbnailUrl ? (
                  <img
                    src={attachedMedia.thumbnailUrl}
                    alt="Attached"
                    className="compose-xpost__attached-thumb"
                  />
                ) : (
                  <div className="compose-xpost__attached-placeholder">
                    {attachedMedia.mediaType === "Video" ? "Video" : "Image"}
                  </div>
                )}
                <span className="compose-xpost__attached-name">
                  {attachedMedia.originalFileName || attachedMedia.mediaType}
                </span>
                <button
                  className="compose-xpost__remove-media"
                  onClick={() => setAttachedMedia(null)}
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                className="compose-xpost__attach-btn"
                onClick={() => setShowMediaBrowser(true)}
              >
                Attach Media
              </button>
            )}
          </div>

          {/* Progress / error */}
          {posting && (
            <div className="compose-xpost__progress">
              Posting to X...
            </div>
          )}
          {error && (
            <div className="compose-xpost__error">{error}</div>
          )}
        </div>

        <div className="compose-xpost__footer">
          <button className="compose-xpost__btn-cancel" onClick={onClose} disabled={posting}>
            Cancel
          </button>
          <button
            className="compose-xpost__btn-post"
            disabled={!tweetText.trim() || isOverLimit || posting}
            onClick={handlePost}
          >
            {posting ? "Posting..." : "Post to X"}
          </button>
        </div>
      </div>

      {showMediaBrowser && (
        <MediaBrowserModal
          isOpen={showMediaBrowser}
          crmProspectProfileId={crmProspectProfileId}
          onSelect={(media) => {
            setAttachedMedia(media);
            setShowMediaBrowser(false);
          }}
          onClose={() => setShowMediaBrowser(false)}
        />
      )}
    </div>
  );
};
